import React from 'react';

import { formatDate as formatDateDisplay, formatTime as formatTimeDisplay } from '@elara/client-core/lib/date-utils';

import { Calendar } from './calendar.js';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from './dropdown-menu.js';
import { FloatingField } from './floating-field.js';
import { IconSizes, MaterialIcon } from './MaterialIcon.js';

function toDateString(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function formatTimeOption(value: string): string {
  return formatTimeDisplay(value, 'h:mma');
}

export interface DateTimeFieldProps {
  label: string;
  /** Selected date as YYYY-MM-DD, or empty string when unset. Ignored entirely when `showDate` is
   * false — pass `''`/a no-op `onDateChange` from the caller in that case. */
  date: string;
  onDateChange: (date: string) => void;
  /** When false the date button/calendar is omitted, leaving just the time select — used for a timed
   * event's End field, which (Google Calendar-style) is time-only and implicitly the same day as Start. */
  showDate?: boolean;
  dateAriaLabel?: string;
  /** When false the time selector is hidden (all-day mode). */
  showTime: boolean;
  /** Selected time as HH:mm. */
  time: string;
  onTimeChange: (time: string) => void;
  /** Available time options as HH:mm strings. Empty array renders "Closed". */
  timeOptions: string[];
  timeAriaLabel?: string;
  /** Month the calendar opens to when no date is selected. */
  defaultMonth?: Date;
  /** Earliest month the calendar allows navigating to. */
  calendarStartMonth?: Date;
  /** Latest month the calendar allows navigating to. */
  calendarEndMonth?: Date;
  disabledDay?: (day: Date) => boolean;
}

export function DateTimeField({
  label,
  date,
  onDateChange,
  showDate = true,
  dateAriaLabel,
  showTime,
  time,
  onTimeChange,
  timeOptions,
  timeAriaLabel,
  defaultMonth,
  calendarStartMonth,
  calendarEndMonth,
  disabledDay,
}: DateTimeFieldProps) {
  const [dateOpen, setDateOpen] = React.useState(false);
  const selectedDateObj = date ? new Date(date) : undefined;

  const handleDayClick = (clickedDate: Date) => {
    onDateChange(toDateString(clickedDate));
    setDateOpen(false);
  };

  return (
    <FloatingField label={label} filled={showDate ? !!date : !!time} forceFloat>
      <div className="flex h-12 items-stretch">
        {showDate && (
          <DropdownMenu open={dateOpen} onOpenChange={setDateOpen}>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label={dateAriaLabel}
                className="flex flex-1 min-w-0 items-center pl-3 pr-2 pt-5 pb-1 text-base focus:outline-none cursor-pointer"
              >
                {date ? (
                  <span className="truncate">{formatDateDisplay(date)}</span>
                ) : (
                  <span className="text-muted-foreground">Date</span>
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="p-0">
              <Calendar
                mode="single"
                defaultMonth={defaultMonth ?? selectedDateObj ?? new Date()}
                {...(calendarStartMonth != null && { startMonth: calendarStartMonth })}
                {...(calendarEndMonth != null && { endMonth: calendarEndMonth })}
                selected={selectedDateObj}
                onDayClick={handleDayClick}
                onSelect={() => {}}
                numberOfMonths={1}
                disabled={disabledDay}
              />
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {showDate && showTime && <div className="mt-5.5 mb-1.5 w-px shrink-0 bg-input" />}

        {showTime && (
          <div className="relative flex-1 flex items-center gap-0.5 pl-3 pr-2 pt-5 pb-1 text-base text-foreground min-w-28">
            <span className="flex-1">
              {time ? formatTimeOption(time) : timeOptions.length === 0 ? 'Closed' : 'Time'}
            </span>
            <MaterialIcon icon="expand_more" size={IconSizes.sm} className="opacity-50 flex-shrink-0" />
            <select
              aria-label={timeAriaLabel}
              value={time}
              disabled={timeOptions.length === 0}
              onChange={(e) => onTimeChange(e.target.value)}
              className="absolute inset-0 cursor-pointer opacity-0 disabled:cursor-not-allowed"
            >
              {timeOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {formatTimeOption(opt)}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
    </FloatingField>
  );
}
